// frontend/pages/FormTPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import {
  BusinessCenter as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  AdminPanelSettings as AdminIcon,
  Person as EmployeeIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import FormTForm from '../components/FormTForm';
import FormTList from '../components/FormTList';
import { formTTheme } from '../css/formTTheme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FormTPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('');
  const [currentEmployeeId, setCurrentEmployeeId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Extract role from token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const role = payload.role || payload.userRole || payload.roleType || 'employee';
      
      setCompanyId(company_id);
      setUserRole(role);

      // Get current user details
      try {
        const response = await axios.get(`${API_URL}/api/user/current`, {
          withCredentials: true,
        });

        if (response.data && response.data.employee_id) {
          setCurrentEmployeeId(response.data.employee_id);
          
          // For employee role, fetch their details
          // For HR role, don't fetch employee details for themselves
          if (role === 'employee') {
            fetchEmployeeDetails(response.data.employee_id, company_id, role);
          } else {
            // For HR, just set loading to false without fetching details
            setLoading(false);
          }
        }
      } catch (apiErr) {
        // Fallback to payload
        const employee_id = payload.employee_id || payload.id || payload.userId;
        if (employee_id) {
          setCurrentEmployeeId(employee_id);
          
          // For employee role, fetch their details
          if (userRole === 'employee') {
            fetchEmployeeDetails(employee_id, company_id, role);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (employeeId, companyId, role) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/employee?employee_id=${employeeId}&role=${role}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeDetails(response.data);
      setLoading(false);
    } catch (error) {
      //console.error('Error fetching employee details:', error);
      // Don't show error for HR since they don't need employee details
      if (role === 'employee') {
        setError(error.response?.data?.error || 'Failed to fetch employee details');
      }
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && userRole === 'employee') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={formTTheme}>
      <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Form T
              </Typography>
              <Typography variant="body1">
                Apply for recovery of gratuity under Payment of Gratuity Act, 1972
              </Typography>
            </Box>
           
          </Box>
        </Paper>

        {/* Employee Information Card - Only for Employee role */}
        {userRole === 'employee' && employeeDetails && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Your Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Name:</strong> {employeeDetails?.employee?.name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Employee ID:</strong> {employeeDetails?.employee?.id}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Joining Date:</strong> {new Date(employeeDetails?.employee?.joiningDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Tenure:</strong> {employeeDetails?.employee?.tenureYears} years
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoneyIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Last Salary:</strong> ₹{employeeDetails?.financials?.lastSalary?.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoneyIcon fontSize="small" color="success" />
                    <Typography variant="body2">
                      <strong>Eligible Gratuity:</strong> ₹{employeeDetails?.financials?.gratuityAmount?.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="📝 New Application" />
            <Tab label="📋 My Applications" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box>
          {activeTab === 0 && (
            <FormTForm 
              employeeDetails={employeeDetails}
              onSuccess={() => setActiveTab(1)}
              userRole={userRole}
              currentEmployeeId={currentEmployeeId}
              companyId={companyId}
            />
          )}
          
          {activeTab === 1 && (
            <FormTList 
              userRole={userRole}
              currentEmployeeId={currentEmployeeId}
              companyId={companyId}
            />
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default FormTPage;